// src/pages/SomePath/DropdownManager.jsx
import React, { useEffect, useState } from "react";
import { RefreshCw, Plus, Edit, Trash2, List } from "lucide-react";
import API from "../../../services/interceptor";
import { useParams, useNavigate } from "react-router-dom";
import AddCategoryItemModal from "../Comman/AddCategoryItemModal";

export default function DropdownManager() {
  const { category: categoryParam } = useParams(); // Get category from URL
  const navigate = useNavigate();
      const [isOpen, setIsOpen] = useState(false);


  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(""); // Renamed to avoid conflict

  const [items, setItems] = useState([]); // raw items (strings or objects)
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal controls
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);

  // Form states for simple lists
  const [newItem, setNewItem] = useState("");
  const [editingValue, setEditingValue] = useState("");

  // For object lists like treatmentPlan (name + price)
  const [newPrice, setNewPrice] = useState("");
  const [editingPrice, setEditingPrice] = useState("");

  // Search/filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // Stats
  const [stats, setStats] = useState({ total: 0, matched: 0 });

  // ------ Helpers ------
  const isObjectList = (arr = items) => Array.isArray(arr) && arr.length > 0 && typeof arr[0] === "object";

  // Helpers (add this)
  const formatCategoryLabel = (key) => {
    if (!key) return "";
    // turn camelCase / snake_case / mixed into words: "allergiesName" -> "Allergies Name"
    const withSpaces = key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase -> space before capital
      .replace(/_/g, " ")                     // snake_case -> spaces
      .replace(/\s+/g, " ")                   // collapse multiple spaces
      .trim();
    return withSpaces
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };


  const getLabel = (it) => {
    if (it == null) return "";
    if (typeof it === "string") return it;
    // Try common fields
    return it.item ?? it.name ?? it.label ?? JSON.stringify(it);
  };

  // show success/error messages
  function showMessage(msg, type = "success") {
    if (type === "success") setSuccess(msg);
    else setError(msg);
    setTimeout(() => {
      setSuccess("");
      setError("");
    }, 4000);
  }

  // ------ Fetch categories on mount ------
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await API.get("/api/dropdown");
        if (res.data) {
          const ignoreKeys = ["_id", "__v", "createdAt", "updatedAt"];
          const fetchedKeys = Object.keys(res.data)
            .filter((key) => !ignoreKeys.includes(key))
            .sort((a, b) => a.localeCompare(b));
          setCategories(fetchedKeys);

          // LOGIC: Set initial category based on URL param or first available
          if (categoryParam && fetchedKeys.includes(categoryParam)) {
            setSelectedCategory(categoryParam);
          } else if (fetchedKeys.length > 0) {
            setSelectedCategory(fetchedKeys[0]);
            // Optional: update URL to match
            navigate(`/dashboard/dropdown-manager/${fetchedKeys[0]}`, { replace: true });
          }
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError(err?.response?.data?.error || "Failed to load dropdown categories from the server.");
      }
    }
    fetchCategories();
  }, [categoryParam, navigate]);



  // ------ Fetch items when category changes ------
  useEffect(() => {
    if (selectedCategory) {
      fetchItems(selectedCategory);
      // reset UI
      setNewItem("");
      setNewPrice("");
      setEditingValue("");
      setEditingPrice("");
      setSelectedIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // ------ Handle category change from dropdown ------
  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    // Update URL when category changes
    navigate(`/dashboard/dropdown-manager/${newCategory}`);
  };

  // ------ Client-side search ------
  useEffect(() => {
    const lower = (searchTerm || "").toLowerCase();
    const f = items.filter((it) => getLabel(it).toLowerCase().includes(lower));
    setFiltered(f);
    setStats({ total: items.length, matched: f.length });
  }, [searchTerm, items]);

  // ------ API helpers ------
  async function fetchItems(cat = selectedCategory) {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/api/dropdown/${encodeURIComponent(cat)}`);
      // server returns { <cat>: [...] }
      const data = res.data?.[cat] ?? [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to load items");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddItem(e) {
    e?.preventDefault?.();
    setError("");
    // if category list is object-based (e.g. treatmentPlan)
    if (isObjectList()) {
      const name = (newItem || "").trim();
      const priceNum = Number(newPrice);
      if (!name) return setError("Name is required");
      if (Number.isNaN(priceNum)) return setError("Price must be a number (use 0 if not available)");

      setActionLoading(true);
      try {
        // send object in body
        const payload = { item: name, price: priceNum };
        const res = await API.put(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, payload);
        setItems(res.data?.[selectedCategory] || []);
        setNewItem("");
        setNewPrice("");
        setShowAddModal(false);
        showMessage("Item added successfully");
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || "Failed to add item");
      } finally {
        setActionLoading(false);
      }
    } else {
      // simple string list
      const value = (newItem || "").trim();
      if (!value) return setError("Item value is required");
      setActionLoading(true);
      try {
        console.log("api: ", `/api/dropdown/${encodeURIComponent(selectedCategory)}`)
        const res = await API.put(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, { item: value });
        setItems(res.data?.[selectedCategory] || []);
        setNewItem("");
        setShowAddModal(false);
        showMessage("Item added successfully");
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || "Failed to add item");
      } finally {
        setActionLoading(false);
      }
    }
  }

  function openEdit(idx) {
    setSelectedIdx(idx);
    const it = items[idx];
    if (typeof it === "string") {
      setEditingValue(it);
      setEditingPrice("");
    } else {
      setEditingValue(getLabel(it));
      setEditingPrice(it.price ?? "");
    }
    setShowEditModal(true);
    setError("");
  }

  function cancelEdit() {
    setSelectedIdx(null);
    setEditingValue("");
    setEditingPrice("");
    setShowEditModal(false);
  }

  async function saveEdit() {
    if (selectedIdx == null) return;
    setError("");
    setActionLoading(true);

    const oldItem = items[selectedIdx];

    try {
      if (typeof oldItem === "string") {
        const newValue = (editingValue || "").trim();
        if (!newValue) {
          setError("New value cannot be empty");
          setActionLoading(false);
          return;
        }
        if (newValue === oldItem) {
          cancelEdit();
          setActionLoading(false);
          return;
        }
        const res = await API.patch(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, {
          oldValue: oldItem,
          newValue,
        });
        setItems(res.data?.[selectedCategory] || []);
      } else {
        // object edit (e.g. treatmentPlan)
        const newName = (editingValue || "").trim();
        const priceNum = Number(editingPrice);
        if (!newName) {
          setError("Name cannot be empty");
          setActionLoading(false);
          return;
        }
        if (Number.isNaN(priceNum)) {
          setError("Price must be a number");
          setActionLoading(false);
          return;
        }
        const newObj = { item: newName, price: priceNum };
        // send objects as oldValue / newValue in body
        const res = await API.patch(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, {
          oldValue: oldItem,
          newValue: newObj,
        });
        setItems(res.data?.[selectedCategory] || []);
      }

      cancelEdit();
      showMessage("Item updated successfully");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to update item");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDelete(idx) {
    const value = items[idx];
    if (!window.confirm(`Are you sure you want to delete "${getLabel(value)}" from the "${selectedCategory}" list?`))
      return;
    setActionLoading(true);
    setError("");
    try {
      // If it's a simple string we can keep using the URL param route for backward compat.
      if (typeof value === "string") {
        const res = await API.delete(
          `/api/dropdown/${encodeURIComponent(selectedCategory)}/${encodeURIComponent(value)}`
        );
        setItems(res.data?.[selectedCategory] || []);
      } else {
        // For objects, send delete body (server must accept JSON in body for DELETE)
        const res = await API.delete(`/api/dropdown/${encodeURIComponent(selectedCategory)}`, { data: { value } });
        setItems(res.data?.[selectedCategory] || []);
      }
      showMessage("Item deleted successfully");
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to delete item");
    } finally {
      setActionLoading(false);
    }
  }

  // ----- Render -----
  return (
    <div className="p-6 ">
      {/* Header */}
      <div className="text-center bg-white rounded-2xl shadow-sm p-6 border">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Dropdown Manager
          </h1>
          <p className="text-gray-600 text-lg">Manage dropdown lists used across the application</p>
        </div>
{/* 
      <AddCategoryItemModal
        isOpen={isOpen}
        selectedCategory="allergiesName"
        onClose={() => setIsOpen(false)}
        onAdded=""
      />
      <button 
      onClick={()=>setIsOpen(!isOpen)}
      >test</button> */}

      {/* Messages & stats */}
      <div className="mb-4 flex gap-4 items-center">
        {success && <div className="text-emerald-700 bg-emerald-100 px-3 py-2 rounded">{success}</div>}
        {error && <div className="text-red-700 bg-red-100 px-3 py-2 rounded">{error}</div>}
        <div className="ml-auto text-sm text-gray-600">Total: {stats.total} · Matched: {stats.matched}</div>
      </div>

      {/* Search + Category select */}
      <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
        <div className="flex flex-col justify-between lg:flex-row gap-4 items-start">
          {/* <div className="flex-1">
            <label className="font-medium block mb-1">Search</label>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
              className="w-full border rounded px-3 py-2"
            />
          </div> */}

          <div className="min-w-[220px] flex items-center gap-3">
            <label className="font-medium">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="border rounded px-3 py-2 flex-1"
              disabled={categories.length === 0}
            >
              {categories.length > 0 ? (
                categories.map((c) => (
                  <option key={c} value={c}>
                    {formatCategoryLabel(c)}
                  </option>
                ))
              ) : (
                <option>Loading categories...</option>
              )}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                fetchItems(selectedCategory);
                setSuccess("");
                setError("");
              }}
              className="flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        </div>
      </div>

      {/* Items list card */}
      <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">Items</h3>
          <div className="text-sm text-gray-500">
            {filtered.length} / {items.length}
          </div>
        </div>

        {loading ? (
          <div className="text-gray-500 py-12 text-center">Loading items...</div>
        ) : items.length === 0 ? (
          <div className="text-gray-500 py-12 text-center">No items in this category.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map((it, idx) => (
              <li key={idx} className="flex items-center justify-between border rounded p-2 hover:shadow-sm">
                <div className="break-words whitespace-pre-wrap w-0 flex-1 mr-4">
                  {typeof it === "string" ? it : `${getLabel(it)} — ₹${it.price ?? 0}`}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(items.indexOf(it))}
                    className="text-sm bg-yellow-100 px-2 py-1 rounded hover:bg-yellow-200"
                    disabled={actionLoading}
                  >
                    <Edit className="h-4 w-4 inline" />
                  </button>
                  <button
                    onClick={() => handleDelete(items.indexOf(it))}
                    className="text-sm bg-red-100 px-2 py-1 rounded hover:bg-red-200"
                    disabled={actionLoading}
                  >
                    <Trash2 className="h-4 w-4 inline" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Item</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <form onSubmit={handleAddItem} className="space-y-4">
              {isObjectList() ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Procedure name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Price (number)"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <input
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Type new item value"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-sky-600 text-white rounded-lg"
                >
                  {actionLoading ? "Adding..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Item</h3>
              <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="space-y-4">
              {isObjectList() ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <input
                      value={editingPrice}
                      onChange={(e) => setEditingPrice(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                  <input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={cancelEdit} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg">
                  Cancel
                </button>
                <button type="button" onClick={saveEdit} disabled={actionLoading} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg">
                  {actionLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      )}
    </div>
  );
}
